import { AnimatePresence, motion } from 'motion/react';
import { useMemo, type ReactNode } from 'react';

import { useAuthEntrance } from '../lib/authEntrance';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeToggle } from './ThemeToggle';
import { AuthBrand } from './AuthBrand';
import { AuthSplitCopy } from './AuthSplitCopy';
import { AuthSplitDecor } from './AuthSplitDecor';

export const AUTH_ROUTE_EASE = [0.22, 1, 0.36, 1] as const;

export const authRouteTransition = {
  duration: 0.68,
  ease: AUTH_ROUTE_EASE,
} as const;

/** Shared by SignIn / SignUp — soft field rise; parent only orchestrates. */
export const authFormTransition = { duration: 0.62, ease: AUTH_ROUTE_EASE } as const;

export const authCardVariants = {
  // Soft card fade (no y) so the shell doesn't read as a second beat, while
  // fields still stagger gently underneath.
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      opacity: { duration: 0.38, ease: AUTH_ROUTE_EASE },
      staggerChildren: 0.09,
      delayChildren: 0.1,
    },
  },
  exit: { opacity: 0, y: -6, transition: { duration: 0.34, ease: AUTH_ROUTE_EASE } },
};

/**
 * SignUp: title + scroll group start together (no form-level stagger slot
 * between them), then the group staggers its fields — one soft wave.
 */
export const authCardVariantsUnified = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      opacity: { duration: 0.38, ease: AUTH_ROUTE_EASE },
      delayChildren: 0.1,
    },
  },
  exit: { opacity: 0, y: -6, transition: { duration: 0.34, ease: AUTH_ROUTE_EASE } },
};

export const authFieldVariants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: authFormTransition },
};

/** Signup scroll body — passes stagger through to fields. */
export const authFieldGroupVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.09 },
  },
};

/** Page forms: play enter once per key (survives Strict Mode remount). */
export function useAuthCardMotion(entranceKey: string, unified = false) {
  const play = useAuthEntrance(entranceKey);
  return useMemo(
    () => ({
      variants: unified ? authCardVariantsUnified : authCardVariants,
      initial: (play ? 'hidden' : false) as 'hidden' | false,
      animate: 'show' as const,
      exit: 'exit' as const,
    }),
    [play, unified],
  );
}

const MotionAuthSplitCopy = motion.create(AuthSplitCopy);

const copyMotionEnter = {
  opacity: 0,
  x: -18,
  filter: 'blur(5px)',
} as const;

const copyMotionRest = {
  animate: { opacity: 1, x: 0, filter: 'blur(0px)' },
  exit: { opacity: 0, x: 12, filter: 'blur(4px)' },
  transition: authRouteTransition,
} as const;

interface AuthSplitShellProps {
  routeKey: string;
  eyebrow: string;
  headline: string;
  body: string;
  panelClassName?: string;
  children: ReactNode;
}

/** Shared auth split chrome — decor stays mounted; copy + form crossfade on route change. */
export function AuthSplitShell({
  routeKey,
  eyebrow,
  headline,
  body,
  panelClassName = 'auth-split-panel--signup',
  children,
}: AuthSplitShellProps) {
  const playCopy = useAuthEntrance(`copy-${routeKey}`);
  const playShell = useAuthEntrance(`shell-${routeKey}`);

  return (
    <div className="auth-screen auth-split">
      <div className="auth-theme-toggle">
        <LanguageSwitcher />
        <ThemeToggle />
      </div>

      <AuthBrand />

      <div className={`auth-split-panel ${panelClassName}`.trim()}>
        <AuthSplitDecor />

        <AnimatePresence mode="wait">
          <MotionAuthSplitCopy
            key={`copy-${routeKey}`}
            eyebrow={eyebrow}
            headline={headline}
            body={body}
            initial={playCopy ? copyMotionEnter : false}
            {...copyMotionRest}
          />
        </AnimatePresence>

        {/*
          No AnimatePresence here — a presence exit on Strict Mode remount made
          the title tab vanish mid-enter, then the page form played again.
          Route changes still remount via key and fade in once.
        */}
        <div className="auth-split-form">
          <motion.div
            key={`form-${routeKey}`}
            className="auth-split-form-stage"
            initial={playShell ? { opacity: 0 } : false}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.42, ease: AUTH_ROUTE_EASE }}
          >
            {children}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
